"use client";

import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { faqCategories, FaqCategory, FaqItem } from "./faqData";

export type SearchResult = {
  category: FaqCategory;
  item: FaqItem;
};

interface FaqSearchBarProps {
  onResults: (results: SearchResult[] | null) => void;
}

export default function FaqSearchBar({ onResults }: FaqSearchBarProps) {
  const [value, setValue] = useState("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const query = value.trim().toLowerCase();
      if (!query) {
        onResults(null);
        return;
      }
      const results: SearchResult[] = [];
      for (const category of faqCategories) {
        for (const item of category.items) {
          if (
            item.question.toLowerCase().includes(query) ||
            item.answer.toLowerCase().includes(query)
          ) {
            results.push({ category, item });
          }
        }
      }
      onResults(results);
    }, 200);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, onResults]);

  const clear = () => {
    setValue("");
    onResults(null);
  };

  return (
    <div className="relative w-full max-w-2xl">
      <Search
        className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
        style={{ color: "var(--color-text-muted)" }}
        aria-hidden="true"
      />
      <input
        type="search"
        aria-label="Search frequently asked questions"
        placeholder="Search questions and answers…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-full pl-10 pr-10 py-3 text-sm rounded-xl border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        style={{
          backgroundColor: "var(--color-bg-elevated)",
          borderColor: "var(--color-border)",
          color: "var(--color-text-primary)",
        }}
      />
      {value && (
        <button
          onClick={clear}
          aria-label="Clear search"
          className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-70"
          style={{ color: "var(--color-text-muted)" }}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
